import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';

const DashboardTracked = ({ showSuccess, showError, showConfirm }) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Styles
  const styles = {
    container: {
      width: '100%',
    },
    sectionHeader: {
      marginBottom: theme.spacing.xl,
      paddingBottom: theme.spacing.lg,
      borderBottom: `2px solid ${theme.colors.borderLight}`,
    },
    sectionTitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize['3xl'],
      color: '#8B4513',
      marginBottom: 0,
    },
    errorBox: {
      padding: theme.spacing.lg,
      backgroundColor: '#FEF2F2',
      color: '#991B1B',
      borderRadius: theme.borderRadius.md,
      marginBottom: theme.spacing.xl,
      border: '1px solid #FEE2E2',
      fontFamily: theme.typography.fontFamily.secondary,
    },
    emptyState: {
      textAlign: 'center',
      padding: `${theme.spacing.xxl} ${theme.spacing.lg}`,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.sm,
    },
    emptyStateTitle: {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize['2xl'],
      marginBottom: theme.spacing.sm,
      color: '#8B4513',
    },
    emptyStateText: {
      marginBottom: theme.spacing.xl,
      color: theme.colors.textSecondary,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: theme.spacing.lg,
    },
    notificationCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.md,
      padding: theme.spacing.lg,
      transition: theme.transitions.base,
      border: `1px solid ${theme.colors.borderLight}`,
    },
    productName: {
      fontSize: theme.typography.fontSize.xl,
      marginBottom: theme.spacing.sm,
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
    },
    productSku: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.lg,
      fontFamily: 'monospace',
      backgroundColor: theme.colors.surfaceAlt,
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      borderRadius: theme.borderRadius.sm,
      display: 'inline-block',
    },
    variantInfo: {
      backgroundColor: '#f9fafb',
      padding: theme.spacing.md,
      borderRadius: theme.borderRadius.md,
      marginBottom: theme.spacing.lg,
      fontSize: theme.typography.fontSize.sm,
      border: `1px solid ${theme.colors.borderLight}`,
    },
    variantLabel: {
      color: theme.colors.textLight,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    variantValue: {
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    statusBadge: {
      marginBottom: theme.spacing.lg,
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.md,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      fontSize: theme.typography.fontSize.sm,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    statusInStock: {
      backgroundColor: '#D1FAE5',
      color: '#065F46',
      border: '2px solid #10B981',
    },
    statusOutOfStock: {
      backgroundColor: '#FEF3C7',
      color: '#92400E',
      border: '2px solid #F59E0B',
    },
    buttonGroup: {
      display: 'flex',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
    },
  };

  const responsiveStyles = `
    @media (max-width: 768px) {
      .tracked-grid {
        grid-template-columns: 1fr !important;
        gap: ${theme.spacing.md} !important;
      }
      
      .tracked-card {
        padding: ${theme.spacing.md} !important;
      }
      
      .tracked-section-title {
        font-size: ${theme.typography.fontSize['2xl']} !important;
      }
      
      .tracked-button-group {
        flex-direction: column;
      }
      
      .tracked-button-group button {
        width: 100%;
      }
    }
    
    @media (max-width: 480px) {
      .tracked-section-title {
        font-size: ${theme.typography.fontSize.xl} !important;
      }
    }
  `;

  useEffect(() => {
    fetchTrackedProducts();
  }, []);

  const fetchTrackedProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.stockNotifications.list(false);
      
      // Check stock status logic from original file
      const updatedData = await Promise.all(
        data.map(async (notification) => {
          try {
            const product = await api.products.getDetail(notification.product_id);
            let isInStock = false;
            
            if (notification.component_variation_id) {
              const components = product.components || [];
              for (const component of components) {
                const variations = component.variations || [];
                for (const variation of variations) {
                  if (variation.id === notification.component_variation_id) {
                    isInStock = variation.stock_qty > 0;
                    break;
                  }
                }
              }
            } else if (notification.variant_id) {
              const subcategories = product.subcategories || [];
              for (const subcat of subcategories) {
                if (subcat.id === notification.variant_id) {
                  isInStock = subcat.stock_qty > 0;
                  break;
                }
              }
            } else {
              isInStock = product.stock_qty > 0;
            }
            
            return { ...notification, is_in_stock: isInStock };
          } catch (err) {
            console.error(`Error checking stock for ${notification.product_id}`, err);
            return notification;
          }
        })
      );
      
      setNotifications(updatedData);
    } catch (err) {
      setError('Failed to load tracked products: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    await showConfirm({
      title: 'Remove Tracking',
      message: 'Are you sure you want to stop tracking this product?',
      type: 'warning',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.stockNotifications.delete(id);
          await fetchTrackedProducts();
          showSuccess('Tracking removed successfully!');
        } catch (err) {
          showError('Failed to remove tracking');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  if (loading && !notifications.length) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div style={{ textAlign: 'center', padding: '5rem 0' }}>
          <div style={{ 
            display: 'inline-block', 
            width: '40px', 
            height: '40px', 
            border: '3px solid #f3f3f3', 
            borderTop: '3px solid #CC5500', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }} />
          <p style={{ color: '#666', fontFamily: theme.typography.fontFamily.secondary }}>Loading tracked products...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
        <div style={styles.sectionHeader}>
          <h2 className="tracked-section-title" style={styles.sectionTitle}>Stock Notifications</h2>
        </div>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {notifications.length === 0 ? (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyStateTitle}>No tracked products</h3>
            <p style={styles.emptyStateText}>You'll be notified here when out-of-stock items become available.</p>
            <Button onClick={() => navigate('/categories')}>
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="tracked-grid" style={styles.gridContainer}>
            {notifications.map((item) => (
              <div 
                key={item.id} 
                className="tracked-card"
                style={styles.notificationCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = theme.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.shadows.md;
                }}
              >
                <h3 style={styles.productName}>
                  {item.product_name}
                </h3>
                <div style={styles.productSku}>
                  SKU: {item.product_sku}
                </div>

                {(item.variant_name || item.component_variation_name) && (
                  <div style={styles.variantInfo}>
                    {item.variant_name && (
                      <div style={{ marginBottom: item.component_variation_name ? theme.spacing.xs : 0 }}>
                        <span style={styles.variantLabel}>Variant: </span>
                        <span style={styles.variantValue}>{item.variant_name}</span>
                      </div>
                    )}
                    {item.component_variation_name && (
                      <div>
                        <span style={styles.variantLabel}>Option: </span>
                        <span style={styles.variantValue}>{item.component_variation_name}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{
                  ...styles.statusBadge,
                  ...(item.is_in_stock ? styles.statusInStock : styles.statusOutOfStock)
                }}>
                  {item.is_in_stock ? (
                    <>
                      <span style={{ fontSize: '1.2em' }}>✓</span>
                      <span>Back in Stock!</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '1.2em' }}>⏳</span>
                      <span>Still Out of Stock</span>
                    </>
                  )}
                </div>

                <div className="tracked-button-group" style={styles.buttonGroup}>
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/product/${item.product_id}`)} 
                    style={{ flex: 1 }}
                  >
                    View Product
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleRemove(item.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardTracked;
